export type Props = {
  className?: string;
};

const DESCRIPTION =
  "Organizations invite Pollinator participants, participants connect with one another, and grantmakers fund them. Together this builds ecosystem resilience.";

/**
 * How the network fits together, in three circles.
 *
 * Organizations invite, participants connect, grantmakers fund, and the box
 * around all three is what that adds up to. It is drawn rather than supplied
 * as a picture so the words in it are the page's own type, stay sharp at any
 * size, and can be read out.
 *
 * It is drawn twice, because the design lays it out twice. From a tablet up
 * the three circles sit in a row and the figure scales as one piece. On a
 * phone a row that wide would shrink its lettering past reading, so the
 * circles are stacked instead and drawn at their own size, organizations
 * above and grantmakers below, each pointing at the participants between
 * them. Only one of the two is shown at any width.
 */
export function MarketingNetworkDiagram({ className }: Props) {
  const label = "fill-brand-800 text-[12px] font-bold uppercase";
  const action = "fill-brand-600 text-[12px] font-bold uppercase";
  const outline = "fill-none stroke-brand-800";
  const flow = "fill-none stroke-brand-600";

  return (
    <div
      data-testid="marketing-network-diagram"
      className={`w-full max-w-[702px] md:max-w-none lg:max-w-[702px] rounded-[32px] border border-brand-800 px-[26px] pt-8 pb-4 md:px-8 ${
        className ?? ""
      }`}
    >
      <svg
        viewBox="0 0 192.5 610.3"
        role="img"
        aria-label={DESCRIPTION}
        data-testid="marketing-network-diagram-stacked"
        className="mx-auto block h-auto w-[192.5px] max-w-full font-sans md:hidden"
      >
        <defs>
          <marker
            id="marketing-network-diagram-stacked-arrow"
            viewBox="0 0 8 8"
            refX="7"
            refY="4"
            markerWidth="8"
            markerHeight="8"
            orient="auto-start-reverse"
          >
            <path d="M0 0 L8 4 L0 8 Z" className="fill-brand-600" />
          </marker>
        </defs>

        <circle cx="96.25" cy="67.9" r="67.4" className={outline} />
        <text x="96.25" y="67.9" textAnchor="middle" dominantBaseline="central" className={label}>
          Organizations
        </text>

        <line
          x1="91.7" y1="135.8" x2="91.7" y2="182.8"
          strokeWidth="1.5"
          className={flow}
          markerEnd="url(#marketing-network-diagram-stacked-arrow)"
        />
        <text x="107.7" y="157.8" dominantBaseline="central" className={action}>
          Invite
        </text>

        <circle cx="96.25" cy="280.05" r="95.75" className={outline} />
        <text x="96.25" y="212" textAnchor="middle" dominantBaseline="central" className={action}>
          Connect
        </text>
        <line
          x1="88.7" y1="225.7" x2="88.7" y2="255.9"
          strokeWidth="1.5"
          className={flow}
          markerEnd="url(#marketing-network-diagram-stacked-arrow)"
        />
        <line
          x1="103.7" y1="255.9" x2="103.7" y2="225.7"
          strokeWidth="1.5"
          className={flow}
          markerEnd="url(#marketing-network-diagram-stacked-arrow)"
        />
        {/* Two lines here, where the row has room for one. */}
        <text textAnchor="middle" dominantBaseline="central" className={label}>
          <tspan x="96.25" y="269.7">Pollinator</tspan>
          <tspan x="96.25" y="289.7">Participants</tspan>
        </text>

        <line
          x1="95.2" y1="424.3" x2="95.2" y2="377.3"
          strokeWidth="1.5"
          className={flow}
          markerEnd="url(#marketing-network-diagram-stacked-arrow)"
        />
        <text x="111.2" y="402.3" dominantBaseline="central" className={action}>
          Fund
        </text>

        <circle cx="96.25" cy="492.2" r="67.4" className={outline} />
        <text x="96.25" y="492.2" textAnchor="middle" dominantBaseline="central" className={label}>
          Grantmakers
        </text>

        <text x="96.25" y="600.3" textAnchor="middle" dominantBaseline="central" className={label}>
          Ecosystem Resilience
        </text>
      </svg>

      <svg
        viewBox="0 0 636 256"
        role="img"
        aria-label={DESCRIPTION}
        data-testid="marketing-network-diagram-row"
        className="mx-auto hidden h-auto w-full md:block md:w-[600px] lg:w-full font-sans"
      >
        <defs>
          <marker
            id="marketing-network-diagram-arrow"
            viewBox="0 0 8 8"
            refX="7"
            refY="4"
            markerWidth="8"
            markerHeight="8"
            orient="auto-start-reverse"
          >
            <path d="M0 0 L8 4 L0 8 Z" className="fill-brand-600" />
          </marker>
        </defs>

        <circle cx="72" cy="102" r="71.5" className={outline} />
        <text x="72" y="102" textAnchor="middle" dominantBaseline="central" className={label}>
          Organizations
        </text>

        <text x="176" y="88" textAnchor="middle" dominantBaseline="central" className={action}>
          Invite
        </text>
        <line
          x1="144" y1="102" x2="215" y2="102"
          strokeWidth="1.5"
          className={flow}
          markerEnd="url(#marketing-network-diagram-arrow)"
        />

        <circle cx="318" cy="102" r="101.5" className={outline} />
        <text x="318" y="41" textAnchor="middle" dominantBaseline="central" className={action}>
          Connect
        </text>
        <line
          x1="310" y1="55" x2="310" y2="86"
          strokeWidth="1.5"
          className={flow}
          markerEnd="url(#marketing-network-diagram-arrow)"
        />
        <line
          x1="326" y1="87" x2="326" y2="56"
          strokeWidth="1.5"
          className={flow}
          markerEnd="url(#marketing-network-diagram-arrow)"
        />
        <text x="318" y="101" textAnchor="middle" dominantBaseline="central" className={label}>
          Pollinator Participants
        </text>

        <text x="460" y="88" textAnchor="middle" dominantBaseline="central" className={action}>
          Fund
        </text>
        <line
          x1="492" y1="102" x2="421" y2="102"
          strokeWidth="1.5"
          className={flow}
          markerEnd="url(#marketing-network-diagram-arrow)"
        />

        <circle cx="564" cy="102" r="71.5" className={outline} />
        <text x="564" y="102" textAnchor="middle" dominantBaseline="central" className={label}>
          Grantmakers
        </text>

        <text x="318" y="246" textAnchor="middle" dominantBaseline="central" className={label}>
          Ecosystem Resilience
        </text>
      </svg>
    </div>
  );
}

export default MarketingNetworkDiagram;
